'use client';

import { ReactNode, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ReactLenis, useLenis } from 'lenis/react';
import DesktopLayout from './layout/Desktop';
import { ProjectModel } from '@/lib/models/Project';
import { SizeProvider } from '@/components/SizeProvider';

function LenisScrollTrigger() {
  // Synchronize Lenis scrolling with GSAP's ScrollTrigger plugin
  const lenis = useLenis(ScrollTrigger.update);

  useEffect(() => {
    if (lenis) {
      // Disable lag smoothing in GSAP to prevent any delay in scroll animations
      gsap.ticker.lagSmoothing(0);
    }
  }, [lenis]);

  return null;
}

function ScrollResetOnRouteChange() {
  const pathname = usePathname();
  const lenis = useLenis();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof window.history?.scrollRestoration !== 'undefined') {
      window.history.scrollRestoration = 'manual';
    }
    lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname, lenis]);

  return null;
}

interface Props {
  children: ReactNode;
  projects: ProjectModel[];
}

export function DesktopShell({ children, projects }: Props) {
  const lenisRef = useRef<any>(null);

  useEffect(() => {
    function update(time: number) {
      lenisRef.current?.lenis?.raf(time * 1000);
    }

    gsap.ticker.add(update);

    return () => gsap.ticker.remove(update);
  }, []);

  return (
    <ReactLenis root ref={lenisRef}>
      <LenisScrollTrigger />
      <ScrollResetOnRouteChange />
      <SizeProvider>
        <DesktopLayout projects={projects}>{children}</DesktopLayout>
      </SizeProvider>
    </ReactLenis>
  );
}
